import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ReviewDecision,
  ReviewState,
  ReviewStatePayload,
  Ticket,
} from "@/types/tickets";
import { ticketKey } from "@/types/tickets";
import { ReviewApiError, reviewApi } from "@/lib/reviewApi";

// localStorage bleibt der **Offline-Cache** und Bootstrap-Quelle bis der Hydrate-
// Call vom Server zurückkommt. Source-of-truth ist die Server-DB (siehe
// maco-runner/review_db.py). Pattern: optimistic local update, dann Server-PUT;
// Fehler im PUT loggen wir, brechen den Klick aber nicht ab -- der nächste
// Hydrate bringt's wieder in Sync.
const STORAGE_KEY = "maco.review-state.v1";

function loadFromStorage(): ReviewStatePayload {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { decisions: {} };
    const parsed = JSON.parse(raw);
    return { decisions: parsed.decisions ?? {} };
  } catch {
    return { decisions: {} };
  }
}

function saveToStorage(payload: ReviewStatePayload) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota überschritten -- selten relevant, weil wir pro Run nur dutzende KB schreiben
  }
}

export type SyncStatus = "idle" | "loading" | "ready" | "error";

export function useReviewState(runId: string) {
  const [payload, setPayload] = useState<ReviewStatePayload>(() => loadFromStorage());
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");
  const [syncError, setSyncError] = useState<string | null>(null);

  // Hydrate beim Mount / wenn runId wechselt: GET vom Backend, ersetzt die
  // lokale Run-Map. Fehler heißt: wir bleiben auf dem localStorage-Stand und
  // markieren den Status als "error" -- die UI kann das anzeigen.
  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    setSyncStatus("loading");
    setSyncError(null);
    reviewApi.list(runId)
      .then((res) => {
        if (cancelled) return;
        // ReviewDecision-Subset extrahieren: Server liefert mehr Felder als der
        // Frontend-Typ kennt, das ist okay -- wir kopieren nur was wir brauchen.
        const cleaned: Record<string, ReviewDecision> = {};
        for (const [key, d] of Object.entries(res.decisions)) {
          cleaned[key] = {
            state: d.state,
            reject_reason: d.reject_reason,
            edits: d.edits,
            reviewed_at: d.reviewed_at,
            reviewed_by: d.reviewed_by,
            jira_key: d.jira_key,
          };
        }
        setPayload((prev) => ({
          decisions: { ...prev.decisions, [runId]: cleaned },
        }));
        setSyncStatus("ready");
      })
      .catch((exc: unknown) => {
        if (cancelled) return;
        const msg = exc instanceof ReviewApiError ? `${exc.detail} (HTTP ${exc.status})` : String(exc);
        setSyncError(msg);
        setSyncStatus("error");
        // Stilllos im localStorage-Modus weiterarbeiten -- Tooltip zeigt den Fehler
        console.warn("review hydrate failed, using localStorage cache:", msg);
      });
    return () => { cancelled = true; };
  }, [runId]);

  // localStorage write-through bei jedem State-Change.
  useEffect(() => {
    saveToStorage(payload);
  }, [payload]);

  const decisions = payload.decisions[runId] ?? {};

  const getDecision = useCallback(
    (t: Pick<Ticket, "aend_id" | "doctype" | "content_hash">): ReviewDecision | undefined => {
      return decisions[ticketKey(t)];
    },
    [decisions]
  );

  // Pending-Sync-Map: wenn der Server gerade nicht erreichbar ist, halten wir
  // die ungesync'ten Keys hier vor und retryen sie beim nächsten setDecision.
  // (Kein Background-Timer -- der nächste echte Klick triggert genug Aktivität.)
  const pendingRetryRef = useRef<Map<string, ReviewDecision | "delete">>(new Map());

  const flushPending = useCallback(async () => {
    if (!runId) return;
    const work = Array.from(pendingRetryRef.current.entries());
    for (const [key, op] of work) {
      try {
        if (op === "delete") {
          await reviewApi.remove(runId, key);
        } else {
          await reviewApi.put(runId, key, {
            state: op.state,
            reject_reason: op.reject_reason,
            edits: op.edits,
          });
        }
        pendingRetryRef.current.delete(key);
      } catch {
        // Bleibt im pending -- nächster Klick retryed wieder
      }
    }
  }, [runId]);

  const setDecision = useCallback(
    (
      t: Pick<Ticket, "aend_id" | "doctype" | "content_hash">,
      decision: ReviewDecision
    ) => {
      const key = ticketKey(t);
      const reviewedAt = decision.reviewed_at ?? new Date().toISOString();
      const enriched: ReviewDecision = { ...decision, reviewed_at: reviewedAt };

      // Optimistic local update
      setPayload((prev) => {
        const runDecisions = { ...(prev.decisions[runId] ?? {}) };
        runDecisions[key] = enriched;
        return { decisions: { ...prev.decisions, [runId]: runDecisions } };
      });

      // Async Server-PUT; bei Fehler in die Retry-Queue
      reviewApi.put(runId, key, {
        state: enriched.state,
        reject_reason: enriched.reject_reason,
        edits: enriched.edits,
      })
        .then(() => { pendingRetryRef.current.delete(key); })
        .catch((exc: unknown) => {
          pendingRetryRef.current.set(key, enriched);
          console.warn("review PUT failed, queued for retry:", exc);
        });

      // Hintergrund-Flush, falls vorherige Klicks pending sind
      void flushPending();
    },
    [runId, flushPending]
  );

  const clearDecision = useCallback(
    (t: Pick<Ticket, "aend_id" | "doctype" | "content_hash">) => {
      const key = ticketKey(t);
      setPayload((prev) => {
        const runDecisions = { ...(prev.decisions[runId] ?? {}) };
        delete runDecisions[key];
        return { decisions: { ...prev.decisions, [runId]: runDecisions } };
      });
      reviewApi.remove(runId, key)
        .then(() => { pendingRetryRef.current.delete(key); })
        .catch((exc: unknown) => {
          pendingRetryRef.current.set(key, "delete");
          console.warn("review DELETE failed, queued for retry:", exc);
        });
    },
    [runId]
  );

  const counts = (tickets: Ticket[]): Record<ReviewState, number> => {
    const out: Record<ReviewState, number> = { pending: 0, approved: 0, rejected: 0, "in-jira": 0 };
    for (const t of tickets) {
      const d = getDecision(t);
      out[d?.state ?? "pending"]++;
    }
    return out;
  };

  /** Export für den CLI-Fallback-Workflow. */
  const exportRunState = (): string => {
    return JSON.stringify({ decisions: decisions }, null, 2);
  };

  /** Setzt alle Entscheidungen eines Runs zurück.
   *
   * Achtung: heute nur lokal! Server bleibt unverändert. Für echten Reset
   * müssten wir N DELETEs feuern -- können wir später ergänzen wenn jemand
   * den Button tatsächlich aus Versehen drückt.
   */
  const resetRun = useCallback(() => {
    setPayload((prev) => {
      const next = { ...prev.decisions };
      delete next[runId];
      return { decisions: next };
    });
  }, [runId]);

  /** JIRA-Sync-Resultat einarbeiten (Server hat die Story schon angelegt UND
   * den Decision-State auf "in-jira" gesetzt). Wir aktualisieren nur den
   * lokalen Cache, kein zusätzlicher PUT nötig. */
  const applyJiraResult = useCallback(
    (created: Array<{ ticket_key: string; jira_key: string; jira_url: string }>) => {
      if (!created.length) return;
      setPayload((prev) => {
        const runDecisions = { ...(prev.decisions[runId] ?? {}) };
        for (const entry of created) {
          const existing = runDecisions[entry.ticket_key];
          runDecisions[entry.ticket_key] = {
            ...(existing ?? { state: "approved" }),
            state: "in-jira",
            jira_key: entry.jira_key,
            reviewed_at: existing?.reviewed_at ?? new Date().toISOString(),
          };
        }
        return { decisions: { ...prev.decisions, [runId]: runDecisions } };
      });
    },
    [runId]
  );

  return {
    decisions,
    getDecision,
    setDecision,
    clearDecision,
    counts,
    exportRunState,
    resetRun,
    applyJiraResult,
    syncStatus,
    syncError,
  };
}
